import React, {useState}              from 'react';
import { useDispatch, useSelector }   from 'react-redux';
import { Modal }                      from 'react-bootstrap';
import classnames                     from 'classnames';

import BomImport                      from './import/BomImport';
import BomQuote                       from './quote/BomQuote';
import ConfirmModal                   from 'pu-react/components/ConfirmModal';
import WarningModal                   from 'shared/pu-react/components/WarningModal';

import {useValidator}                 from 'shared/pu-utils/use_validator';

import {w_title_options}              from '../data/SelectOptions';

import { 
  IMPORTING,
  QUOTED,
  QUOTING_BOM_PARTS,
} from '../data/BomStatus';

import { importBom } from '../slice/import _bom';

const BomModal = (props) => {
  const dispatch = useDispatch()
  const [warnings, setWarnings] = useState([]);
  const [showWarning, setShowWarning] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [numberBomErrors, setNumberBomErrors] = useState(0);

  const [validator, forceUpdate] = useValidator({}, {
    gt: {
      message: 'The :attribute must be greater than :min:type.',
      rule: (val, params, validator) => validator.helpers.size(val, params[1]) > parseFloat(params[0]),
      messageReplace: (message, params) => message.replace(':min', params[0]).replace(':type', validator.helpers.sizeText(params[1]))
    },
  })

  const {
    bom_line_items,
    bom_parameters,
    bom_status,
    loading,
    messages,
    selected_board_qty,
    saved,
    show,
    onHide,
    onShow,
    onShowSearchModal,
    onShowMoreOptions,
    onSaveChanges,
    onCancelChanges,
  } = props;

  const onImport = () => {
    setWarnings([])
    let import_warnings = []

    if(!bom_parameters.includes('reference_designator')) import_warnings.push('Please select Reference Designator column.')
    if(!bom_parameters.includes('mpn')) import_warnings.push('Please select Manufacturer Part Number column.')
    if(!bom_parameters.includes('qty_per_board')) import_warnings.push('Please select Quantity Per Board column.')
    
    let duplicate_bom_params = {}
    let duplicates = []
    bom_parameters.forEach((param) => {
      if(!duplicate_bom_params[param]) duplicate_bom_params[param] = 0
      duplicate_bom_params[param] += 1
      if(param != "" && duplicate_bom_params[param] > 1 && !duplicates.includes(param)) duplicates.push(param)
    })
    if(duplicates.length > 0) {
      const duplicated_columns = duplicates.map((param) => w_title_options.find((option) => option.value == param).text).join(", ")
      import_warnings.push(`Please select only one of each column: ${duplicated_columns}.`)
    }

    if(!validator.allValid()) {
      validator.showMessages();
      if(import_warnings.length == 0) return;
    }

    if(import_warnings.length > 0) {
      setWarnings(import_warnings)
      setShowWarning(true)
      return;
    }

    dispatch(importBom())
  }

  const onSave = () => {
    if(showConfirm) setShowConfirm(false)
    onSaveChanges()
  }

  const onClickSave = () => {
    const unselected_blis = bom_line_items.filter((bli) => bli.user_action == "")
    if(unselected_blis.length > 0){
      setWarnings(["There are parts that you haven't selected an action.", "Please select an action for all unselected parts."])
      setShowWarning(true)
      return;
    }
    onSave()
  }

  const onHideModal = () => {
    const unselected_blis = bom_line_items.filter((bli) => bli.user_action == "")
    if(bom_status == QUOTED && unselected_blis.length > 0){
      setWarnings(["There are parts that you haven't selected an action.", "Please select an action for all unselected parts."])
      setShowWarning(true)
      return;
    }
    onHide()
  }

  const onCancel = () => {
    if(bom_status == IMPORTING){
      onHideModal()
      return;
    }

    if(bom_status == QUOTED){
      setShowConfirm(true)
      return;
    }
  }

  const onCancelImport = () => {
    onHideModal()
  }

  const onCancelQuote = () => {
    setShowConfirm(true)
  }

  const onConfirmCancel = () => {
    setShowConfirm(false)
    onCancelChanges()
  }

  return (
    <Modal 
      className={classnames("file-extractor-modal bom-modal", {
        "import-view": bom_status == IMPORTING,
      })}
      show={show} 
      onShow={onShow}
      onHide={onHideModal}
      size="xl" 
      centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {bom_status == IMPORTING && "IMPORT & QUOTE BOM" }
          {bom_status == QUOTING_BOM_PARTS && "QUOTING BOM"}
          {bom_status == QUOTED && "BOM QUOTED"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {bom_status == IMPORTING ? 
          <BomImport 
            bom_parameters={bom_parameters} 
            validator={validator}/> 
          : <BomQuote 
              bom_line_items={bom_line_items} 
              bom_status={bom_status}
              messages={messages}
              selected_board_qty={selected_board_qty}
              onShowSearchModal={onShowSearchModal}
              onShowMoreOptions={onShowMoreOptions}/>
        }
        <WarningModal
          show={showWarning}
          onClickOk={() => setShowWarning(false)}>
          <h4 className="modal-title mb-3">Warning</h4>
          <p className="text-start">
            {warnings && warnings.map((warning, index) => (
              <span key={`warning_${index}`}>{warning}<br/></span> 
            ))}
          </p>
        </WarningModal>
      </Modal.Body>
      <Modal.Footer className="justify-content-between row">
        <div className='col-5'/>
        <div className='col-6 d-flex justify-content-end'>
          { bom_status == IMPORTING && 
            <button type="button" 
              className="btn btn-outline-primary" 
              onClick={onCancelImport}
              disabled={loading}>
              CANCEL
            </button>
          }
          { bom_status == QUOTED && saved && 
            <button type="button" 
              className="btn btn-outline-primary" 
              onClick={onCancelQuote}
              disabled={loading}>
              CANCEL
            </button>
          }
          {bom_status == IMPORTING && 
            <button type="button" 
              className="btn btn-primary ms-3" 
              onClick={onImport}
              disabled={loading}>
              {loading ? "Importing..." : "IMPORT & QUOTE NOW"}
            </button>
          }
          {bom_status == QUOTED && 
            <>
              <button type="button" 
                className="btn btn-primary ms-3" 
                onClick={onClickSave}
                disabled={loading}>
                {loading ? "Saving..." : "SAVE & CONTINUE"}
              </button>
              <ConfirmModal 
                show={showConfirm}
                variant_no="outline-primary"
                onClickYes={onConfirmCancel}
                onClickNo={() => setShowConfirm(false)}>
                <h4 className="fw-bold mb-3">Warning</h4>
                <p className="text-center lead confirm-text-size">
                  {saved ? (
                    <>
                      Your changes on your quote won't be saved.<br/>
                      Are you sure you want to not save these changes?
                    </>
                  ) : (
                    <>  
                      This action will delete your quote for your BOM and can't be undone.<br/>
                      Are you sure you want to cancel the quote?
                    </>
                  )}
                </p>
              </ConfirmModal>
            </>
            
          }
        </div>   
      </Modal.Footer>
    </Modal>
  )
}

export default BomModal;